import React, { useEffect, useMemo, useState } from "react";
import { FaBell, FaCheckCircle, FaClock, FaExchangeAlt, FaUserCircle } from "react-icons/fa";
import api from "../api";

const ACTION_LABELS = {
  CLAIM: 'Tarefa assumida',
  SUPERVISOR_CLAIM: 'Tarefa assumida por supervisor',
  REASSIGN: 'Tarefa reatribuída',
  RELEASE: 'Tarefa liberada',
  SUPERVISOR_RELEASE: 'Tarefa liberada por supervisor',
  SUPERVISOR_TAKEOVER: 'Tarefa assumida por supervisor',
};

const HIDDEN_CHANGE_KEYS = new Set([
  'action',
  'previousAssigneeId',
  'previousAssigneeName',
  'targetUserId',
  'targetUserName',
]);

const SENSITIVE_CHANGE_KEYS = new Set([
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'authtoken',
  'bearertoken',
  'resettoken',
  'passwordresettoken',
  'verificationtoken',
  'csrftoken',
  'authorization',
  'authorizationheader',
  'password',
  'passwordhash',
  'passwd',
  'senha',
  'secret',
  'clientsecret',
  'apikey',
  'xapikey',
  'jwt',
  'cookie',
  'setcookie',
  'sessioncookie',
  'sessionid',
  'credential',
  'credentials',
  'privatekey',
]);

const normalizeChangeKey = (key) => String(key || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]/g, '')
  .toLowerCase();

const isSensitiveChangeKey = (key) => {
  const normalized = normalizeChangeKey(key);
  return SENSITIVE_CHANGE_KEYS.has(normalized)
    || normalized.endsWith('token')
    || normalized.endsWith('password')
    || normalized.endsWith('passwordhash')
    || normalized.endsWith('secret')
    || normalized.endsWith('apikey')
    || normalized.endsWith('privatekey');
};

const redactSensitiveString = (value) => value
  .replace(/\b(Bearer|Basic)\s+[A-Za-z0-9._~+/-]+=*/gi, '$1 [REDACTED]')
  .replace(
    /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g,
    '[REDACTED]',
  )
  .replace(
    /([?&](?:access_?token|refresh_?token|id_?token|auth_?token|token|api_?key|password|senha|secret)=)[^&#\s]+/gi,
    '$1[REDACTED]',
  );

const sanitizeHistoryDisplayValue = (value) => {
  if (typeof value === 'string') return redactSensitiveString(value);
  if (Array.isArray(value)) return value.map((entry) => sanitizeHistoryDisplayValue(entry));
  if (!value || typeof value !== 'object') return value;

  return Object.entries(value).reduce((result, [key, entry]) => {
    if (!isSensitiveChangeKey(key)) {
      result[key] = sanitizeHistoryDisplayValue(entry);
    }
    return result;
  }, {});
};

const shouldHideChangeKey = (key) => HIDDEN_CHANGE_KEYS.has(key) || isSensitiveChangeKey(key);

const formatFieldName = (key) => String(key || '')
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, (char) => char.toUpperCase());

const normalizeValue = (value) => {
  if (value === undefined || value === null || value === '') return '—';
  if (value instanceof Date) return value.toLocaleString();
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value.map((item) => normalizeValue(item)).join(', ');
  }
  if (typeof value === 'object') {
    if (typeof value.name === 'string') return value.name;
    if (typeof value.label === 'string') return value.label;
    if (typeof value.fileName === 'string') return value.fileName;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
};

const valuesAreEqual = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

export default function StepHistory({
  instanceId,
  onOpenDetails,
  historyData = null,
  title = 'Histórico da solicitação',
  description = 'Linha do tempo com responsáveis, alterações registradas e eventos da tarefa.',
  emptyMessage = 'Nenhuma etapa executada.',
}) {
  const hasProvidedHistory = Boolean(
    historyData && typeof historyData === 'object',
  );
  const [history, setHistory] = useState(() =>
    Array.isArray(historyData?.steps) ? historyData.steps : [],
  );
  const [responsibles, setResponsibles] = useState(() =>
    historyData?.stepResponsibles &&
    typeof historyData.stepResponsibles === 'object'
      ? historyData.stepResponsibles
      : {},
  );
  const [stepNames, setStepNames] = useState(() =>
    historyData?.stepNames && typeof historyData.stepNames === 'object'
      ? historyData.stepNames
      : {},
  );
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(!hasProvidedHistory);

  const parseResponsible = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return { userIds: [], groupIds: [], sectorIds: [] };
    }
    const userIds = new Set();
    const groupIds = new Set();
    const sectorIds = new Set();
    const add = (value, target) => {
      if (typeof value === 'number' && Number.isFinite(value)) target.add(value);
      if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) target.add(parsed);
      }
    };
    const addMany = (values, target) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => add(value, target));
    };

    add(entry.userId, userIds);
    add(entry.groupId, groupIds);
    add(entry.sectorId, sectorIds);
    addMany(entry.userIds, userIds);
    addMany(entry.groupIds, groupIds);
    addMany(entry.sectorIds, sectorIds);

    return {
      userIds: Array.from(userIds),
      groupIds: Array.from(groupIds),
      sectorIds: Array.from(sectorIds),
    };
  };

  const getResponsibleName = (step) => {
    const entry = responsibles[String(step)];
    if (!entry) return null;
    const { userIds, groupIds, sectorIds } = parseResponsible(entry);
    const parts = [];
    userIds.forEach((id) => {
      const u = users.find((userItem) => userItem.id === id);
      parts.push(u ? `Usuário: ${u.name}` : `Usuário #${id}`);
    });
    groupIds.forEach((id) => {
      const g = groups.find((groupItem) => groupItem.id === id);
      parts.push(g ? `Grupo: ${g.name}` : `Grupo #${id}`);
    });
    sectorIds.forEach((id) => {
      const sector = sectors.find((item) => item.id === id);
      parts.push(sector ? `Setor: ${sector.name}` : `Setor #${id}`);
    });
    if (!parts.length) return null;
    return parts.join(', ');
  };

  const usersById = useMemo(() => {
    const map = new Map();
    users.forEach((userItem) => {
      if (userItem && typeof userItem.id !== 'undefined') {
        map.set(Number(userItem.id), userItem);
      }
    });
    return map;
  }, [users]);

  const getPerformerLabel = (entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const directName = typeof entry.userName === 'string' ? entry.userName.trim() : '';
    if (directName) return directName;

    const rawUserId = entry.userId ?? entry.data?.userId;
    const numericId = Number(rawUserId);
    if (Number.isFinite(numericId)) {
      const match = usersById.get(numericId);
      if (match?.name) {
        return match.name;
      }
      return `Usuário #${numericId}`;
    }
    return null;
  };

  useEffect(() => {
    if (hasProvidedHistory) {
      setHistory(Array.isArray(historyData?.steps) ? historyData.steps : []);
      setResponsibles(
        historyData?.stepResponsibles &&
        typeof historyData.stepResponsibles === 'object'
          ? historyData.stepResponsibles
          : {},
      );
      setStepNames(
        historyData?.stepNames && typeof historyData.stepNames === 'object'
          ? historyData.stepNames
          : {},
      );
      setLoading(false);
      return undefined;
    }

    if (!instanceId) {
      setHistory([]);
      setResponsibles({});
      setStepNames({});
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);

    const fetchHistory = async () => {
      try {
        const res = await api.get(
          `/processes/instances/${instanceId}/history`
        );
        if (!active) return;
        setHistory(res.data.steps || []);
        setResponsibles(res.data.stepResponsibles || {});
        setStepNames(res.data.stepNames || {});
      } catch {
        if (!active) return;
        setHistory([]);
        setResponsibles({});
        setStepNames({});
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchHistory();

    return () => {
      active = false;
    };
  }, [hasProvidedHistory, historyData, instanceId]);

  useEffect(() => {
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setUsers([]));
    api.get('/groups').then((res) => setGroups(res.data)).catch(() => setGroups([]));
    api.get('/sectors').then((res) => setSectors(res.data)).catch(() => setSectors([]));
  }, []);

  if (loading) return <p>Carregando...</p>;
  if (history.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">{title}</p>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            0 registros
          </span>
        </div>
        <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const formatEntryLabel = (entry) => {
    const dateLabel = entry.date ? ` (${new Date(entry.date).toLocaleString()})` : '';
    if (entry.eventId && entry.eventId !== 'mail.notification') {
      return `Evento ${entry.eventId}${dateLabel}`;
    }
    const name = stepNames[String(entry.step)];
    const normalizedName =
      typeof name === 'string' && name.trim() ? name.trim() : '';
    const label = normalizedName || 'Etapa';
    return `${label}${dateLabel}`;
  };

  const normalizeMailData = (entry) => {
    const data = entry && typeof entry === 'object' ? entry.data : null;
    if (!data || typeof data !== 'object') return {};
    return data;
  };

  const decoratedHistory = history.map((entry, index) => {
    const previousData = history
      .slice(0, index)
      .reduce(
        (acc, item) => ({
          ...acc,
          ...sanitizeHistoryDisplayValue(item?.data || {}),
        }),
        {},
      );
    const data = entry?.data && typeof entry.data === 'object'
      ? sanitizeHistoryDisplayValue(entry.data)
      : {};
    const action = typeof data.action === 'string' ? data.action : null;
    const changes = Object.keys(data)
      .filter((key) => !shouldHideChangeKey(key))
      .map((key) => ({ key, before: previousData[key], after: data[key] }))
      .filter((change) => !valuesAreEqual(change.before, change.after));

    return { entry, action, changes, data };
  });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
          {history.length} registro{history.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="relative space-y-4 before:absolute before:bottom-2 before:left-5 before:top-2 before:w-px before:bg-slate-200">
        {decoratedHistory.map(({ entry: h, action, changes, data }, idx) => {
          const performer = getPerformerLabel(h) || 'Sistema';
          const timestamp = h.date ? new Date(h.date).toLocaleString() : null;
          const responsible = !h.eventId ? getResponsibleName(h.step) : null;
          const isMail = h.eventId === 'mail.notification';
          const title = action ? ACTION_LABELS[action] || 'Atualização de atribuição' : formatEntryLabel(h).replace(/ \([^)]*\)$/, '');
          const Icon = isMail ? FaBell : action ? FaExchangeAlt : FaCheckCircle;

          return (
            <div key={idx} className="relative flex gap-3">
              <div className="z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-100 bg-blue-50 text-blue-600 shadow-sm">
                <Icon />
              </div>
              <div className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{title}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 ring-1 ring-slate-200"><FaUserCircle /> {performer}</span>
                      {timestamp && <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 ring-1 ring-slate-200"><FaClock /> {timestamp}</span>}
                      {responsible && <span className="rounded-full bg-white px-2 py-1 ring-1 ring-slate-200">Responsável: {responsible}</span>}
                    </div>
                  </div>
                  {onOpenDetails && (
                    <button onClick={() => onOpenDetails(idx)} className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                      Detalhes
                    </button>
                  )}
                </div>

                {h.deadline && (
                  <p className="mt-3 text-xs text-slate-500">
                    Prazo: <span className={new Date(h.date) > new Date(h.deadline) ? 'font-semibold text-red-600' : 'font-semibold text-slate-700'}>{new Date(h.deadline).toLocaleString()}</span>
                  </p>
                )}

                {isMail ? (
                  (() => {
                    const data = normalizeMailData(h);
                    const rows = [
                      ['Mensagem', data.message || 'Notificação de e-mail'],
                      ['Contexto', data.context === 'assignment' ? 'Nova tarefa' : data.context === 'step_available' ? 'Nova etapa disponível' : data.context === 'responsible_update' ? 'Atualização para responsáveis' : data.context],
                      ['Etapa', data.stepName],
                      ['Destinatário', data.recipient],
                      ['Assunto', data.subject],
                      ['Erro', data.error],
                    ].filter(([, value]) => value);
                    return <div className="mt-3 grid gap-2 sm:grid-cols-2">{rows.map(([label, value]) => <div key={label} className="rounded-xl bg-white p-3 text-xs ring-1 ring-slate-200"><span className="block font-semibold uppercase tracking-wide text-slate-400">{label}</span><span className={label === 'Erro' ? 'text-rose-600' : 'text-slate-700'}>{value}</span></div>)}</div>;
                  })()
                ) : changes.length ? (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Alterações registradas</p>
                    <div className="grid gap-2">
                      {changes.map((change) => (
                        <div key={change.key} className="rounded-xl border border-slate-200 bg-white p-3">
                          <p className="text-xs font-semibold text-slate-700">{formatFieldName(change.key)}</p>
                          <div className="mt-2 grid gap-2 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
                            <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600"><span className="mb-1 block font-semibold text-slate-400">Antes</span><pre className="whitespace-pre-wrap font-sans">{normalizeValue(change.before)}</pre></div>
                            <span className="hidden items-center text-slate-300 md:flex">→</span>
                            <div className="rounded-lg bg-blue-50 p-2 text-xs text-blue-900"><span className="mb-1 block font-semibold text-blue-500">Depois</span><pre className="whitespace-pre-wrap font-sans">{normalizeValue(change.after)}</pre></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="mt-3 rounded-xl bg-white p-3 text-xs text-slate-500 ring-1 ring-slate-200">
                    Nenhum campo de formulário foi alterado neste registro.
                    {action && data.targetUserName ? ` Novo responsável: ${data.targetUserName}.` : ''}
                    {action && data.previousAssigneeName ? ` Responsável anterior: ${data.previousAssigneeName}.` : ''}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
